import React from 'react';
import { useTranslation } from 'react-i18next';

import { Sheet, useSheetDismiss } from '@/nova/components/Sheet';
import { Button, Text } from '@/nova/components';
import { Body, DangerButton, FooterRow } from './CancelUpgradeModal.styles';

export type CancelUpgradeModalProps = {
  open: boolean;
  /** Feature name, interpolated into the body copy. */
  name: string;
  /** The tier the pending upgrade would activate ("Pro"). */
  tierName: string;
  /** Pre-formatted invoice amount (e.g. "$59.99 USD"), interpolated into the body copy. */
  amount: string;
  /** Cancel request in flight — disables the actions and blocks a double submit. */
  busy?: boolean;
  onClose: () => void;
  /** Confirm cancellation — voids the pending invoice via `invoice/cancel` (terminal). */
  onConfirm: () => void;
};

/**
 * Confirmation dialog for cancelling a feature's pending upgrade: the invoice was minted but
 * never paid, so cancelling voids it (the feature stays where it is, and nothing is charged).
 * Cancelling is terminal for that invoice, so it asks first. Mirrors the `DeactivateModal`:
 * a centred Overlay with an outline dismiss and a solid red confirm CTA.
 */
export const CancelUpgradeModal = ({
  open,
  name,
  tierName,
  amount,
  busy = false,
  onClose,
  onConfirm,
}: CancelUpgradeModalProps): React.ReactElement | null => {
  const { t } = useTranslation();
  // Dismiss through the overlay so the dialog slides out; wired straight to onClose it
  // would unmount on the spot and blink away.
  const { closeHandle, dismiss } = useSheetDismiss(onClose);

  if (!open) {
    return null;
  }

  return (
    <Sheet
      // The Overlay re-runs the title through t() (a no-op on an already-resolved string);
      // wrapping it here is what makes the key extractable (prop literals aren't harvested).
      title={t('Cancel this upgrade?')}
      onClose={onClose}
      closeHandle={closeHandle}
      footer={
        <FooterRow>
          <Button variant="outline" label={t('Keep upgrade')} disabled={busy} onClick={dismiss} />
          <DangerButton type="button" disabled={busy} onClick={onConfirm}>
            {/* Literal `t()` calls so both labels are harvested (a dynamic `text={…}`
                expression is not extracted — see docs/i18n.md). */}
            <Text text={busy ? t('Cancelling…') : t('Cancel upgrade')} tag="span" />
          </DangerButton>
        </FooterRow>
      }
    >
      <Body
        text="Your pending {{name}} {{tier}} upgrade won't go through and the {{amount}} invoice will be cancelled. This can't be undone, and you won't be charged."
        placeholders={{ name, tier: tierName, amount }}
      />
    </Sheet>
  );
};

export default CancelUpgradeModal;
